/**
 * ICT overlay renderer — PO3 phases (vertical shaded bands), killzones
 * (vertical bands tied to a session), Inverse FVGs, OTE retracement zone,
 * and the Market Structure Shift (MSS) line.
 */
import type { ICTOverlay } from '@trading/shared';
import type { OverlayDrawContext } from './types';

const KILLZONE_COLORS: Record<string, string> = {
  london: '#3b82f6',
  'new-york-am': '#22c55e',
  'london-close': '#a855f7',
  'new-york-pm': '#facc15',
  asia: '#94a3b8',
};

const PO3_COLORS: Record<string, string> = {
  accumulation: '#3b82f6',
  manipulation: '#f97316',
  distribution: '#a855f7',
};

export function drawICTOverlay(data: ICTOverlay, c: OverlayDrawContext): void {
  const { ctx, height, defaultZoneStartX, zoneEndX, getYFromPrice, getXFromTime } = c;

  // PO3 phases — vertical bands across the full chart height with a soft tint
  // and a label at the top.
  data.po3?.forEach((phase) => {
    const startX = getXFromTime(phase.startTime, defaultZoneStartX);
    const endX = getXFromTime(phase.endTime, zoneEndX);
    if (endX <= startX) return;
    const color = PO3_COLORS[phase.phase] ?? '#94a3b8';
    ctx.save();
    ctx.fillStyle = `${color}14`; // ~8% alpha
    // If a price band was supplied, shade only that band; otherwise shade the
    // full chart height to communicate "this whole region is in phase X".
    const top = phase.topPrice != null ? getYFromPrice(phase.topPrice) : 0;
    const bot = phase.bottomPrice != null ? getYFromPrice(phase.bottomPrice) : height;
    const yTop = top ?? 0;
    const yBot = bot ?? height;
    ctx.fillRect(startX, Math.min(yTop, yBot), endX - startX, Math.abs(yBot - yTop));
    // Label at the top
    ctx.fillStyle = `${color}cc`;
    ctx.font = 'bold 10px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`PO3: ${phase.phase.toUpperCase()}`, startX + 4, 6);
    ctx.restore();
  });

  // Killzones — vertical bands across full height, distinct accent per session.
  data.killzones?.forEach((kz) => {
    const startX = getXFromTime(kz.startTime, defaultZoneStartX);
    const endX = getXFromTime(kz.endTime, zoneEndX);
    if (endX <= startX) return;
    const color = KILLZONE_COLORS[kz.name] ?? '#94a3b8';
    ctx.save();
    ctx.fillStyle = `${color}1f`; // ~12% alpha
    ctx.fillRect(startX, 0, endX - startX, height);
    // Vertical accent borders
    ctx.fillStyle = `${color}80`;
    ctx.fillRect(startX, 0, 1, height);
    ctx.fillRect(endX - 1, 0, 1, height);
    // Label at top, just inside the band
    ctx.fillStyle = color;
    ctx.font = 'bold 10px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`KZ ${kz.name}`, startX + 4, 22);
    ctx.restore();
  });

  // Inverse FVGs — same as FVG but with a distinctive cross-hatch dash.
  data.ifvg?.forEach((gap) => {
    const topY = getYFromPrice(gap.top);
    const bottomY = getYFromPrice(gap.bottom);
    if (topY === null || bottomY === null) return;
    const y = Math.min(topY, bottomY);
    const h = Math.abs(bottomY - topY);
    if (!Number.isFinite(y) || !Number.isFinite(h) || h < 2) return;
    const startX = getXFromTime(gap.time, defaultZoneStartX);
    const w = Math.max(zoneEndX - startX, 80);
    ctx.save();
    // IFVG flips the original color (bullish gap → bearish role).
    const color = gap.type === 'bullish' ? '#f97316' : '#3b82f6';
    ctx.fillStyle = `${color}10`;
    ctx.fillRect(startX, y, w, h);
    ctx.strokeStyle = `${color}80`;
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 6]);
    ctx.strokeRect(startX, y, w, h);
    ctx.setLineDash([]);
    ctx.font = 'bold 11px Inter, sans-serif';
    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText('IFVG', startX + 6, y + 4);
    ctx.restore();
  });

  // OTE zone — golden retracement band between 0.62 and 0.79.
  if (data.ote) {
    const topY = getYFromPrice(data.ote.topPrice);
    const bottomY = getYFromPrice(data.ote.bottomPrice);
    if (topY !== null && bottomY !== null) {
      const y = Math.min(topY, bottomY);
      const h = Math.abs(bottomY - topY);
      const startX = getXFromTime(data.ote.time, defaultZoneStartX);
      ctx.save();
      ctx.fillStyle = '#eab30822'; // golden glow
      ctx.fillRect(startX, y, zoneEndX - startX, h);
      ctx.strokeStyle = '#eab308a0';
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(startX, y, zoneEndX - startX, h);
      ctx.setLineDash([]);
      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.fillStyle = '#eab308';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText('OTE 0.62–0.79', startX + 6, y + 4);
      ctx.restore();
    }
  }

  // MSS — labelled marker at the structural shift price.
  if (data.mss) {
    const y = getYFromPrice(data.mss.price);
    if (y !== null && y >= 0 && y <= height) {
      ctx.save();
      const color = data.mss.type === 'bullish' ? '#22c55e' : '#ef4444';
      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.fillStyle = color;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = color;
      ctx.shadowBlur = 6;
      ctx.fillText('MSS', zoneEndX - 5, y);
      ctx.restore();
    }
  }
}
